import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  completePortalPayflowSession,
  createPortalPayflowSession,
  previewPortalPayment,
} from '../../../endpoints/portalLoans';
import type {
  PortalPayConfirmState,
  PortalPaymentPreviewResponse,
  PortalPayflowSessionResponse,
} from '../../../types/portalLoans';
import { getApiErrorMessage } from '../../../utils/httpClientTs';
import PayflowHostedCardFrame, {
  type PayflowHostedMessage,
} from '../../../Components/PayflowHostedCardFrame';
import { formatPortalCurrency } from '../mock/store';
import { ELF_TEAL, elfPrimaryButtonSx } from '../elfTheme';

const fmtMoney = (n: number) => formatPortalCurrency(n);

const LoanPayConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const draft = (location.state || null) as PortalPayConfirmState | null;

  const [preview, setPreview] = useState<PortalPaymentPreviewResponse | null>(
    null
  );
  const [previewPending, setPreviewPending] = useState(false);
  const [session, setSession] = useState<PortalPayflowSessionResponse | null>(
    null
  );
  const [sessionPending, setSessionPending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState(0);

  const draftAmount = draft?.amount;

  useEffect(() => {
    if (draftAmount == null || draftAmount <= 0) return;
    let cancelled = false;
    setPreviewPending(true);
    setError(null);
    previewPortalPayment(draftAmount)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setError(getApiErrorMessage(err) || 'Unable to preview payment.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftAmount]);

  const handleStartCard = async () => {
    if (!draft || draft.amount <= 0) return;
    setError(null);
    setSession(null);
    setSessionPending(true);
    try {
      const data = await createPortalPayflowSession(draft.amount);
      setSession(data);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Unable to start card checkout.');
    } finally {
      setSessionPending(false);
    }
  };

  const finalize = useCallback(async () => {
    if (!session || completing) return;
    setCompleting(true);
    setError(null);
    try {
      await completePortalPayflowSession(session.session_id);
      setReceiptAmount(draft?.amount ?? 0);
      setDone(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] }),
        queryClient.invalidateQueries({ queryKey: ['portal-loans-history'] }),
      ]);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Unable to complete payment.');
      setSession(null);
    } finally {
      setCompleting(false);
    }
  }, [session, completing, draft?.amount, queryClient]);

  const onPayflowMessage = useCallback(
    (msg: PayflowHostedMessage) => {
      if (msg.status === 'approved') {
        void finalize();
        return;
      }
      if (msg.status === 'canceled') {
        setError('Card entry was canceled.');
        setSession(null);
        return;
      }
      setError('Card was not approved. You can try again.');
      setSession(null);
    },
    [finalize]
  );

  if (!draft && !done) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No payment details found. Start from Make a payment.
        </Alert>
        <Button
          component={RouterLink}
          to="/portal/loans/pay"
          variant="contained"
        >
          Make a payment
        </Button>
      </Box>
    );
  }

  if (done) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Payment submitted
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your card payment was applied to your ELF account.
          </Alert>
          <Typography variant="h5" gutterBottom>
            {fmtMoney(receiptAmount)}
          </Typography>
          <Typography color="text.secondary" paragraph>
            You can review the posting on your payment history. A confirmation
            email is not sent yet.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              component={RouterLink}
              to="/portal/loans/history"
              variant="contained"
              sx={elfPrimaryButtonSx}
            >
              View history
            </Button>
            <Button component={RouterLink} to="/portal/loans">
              Back to loan home
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Confirm payment
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Review the allocation, then enter your card details securely.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {fmtMoney(draft!.amount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Method
        </Typography>
        <Typography sx={{ mb: 2 }}>Debit / credit card</Typography>
        <Typography variant="body2" color="text.secondary">
          Type
        </Typography>
        <Typography sx={{ mb: 2 }}>
          {draft!.amountMode === 'installment'
            ? 'Scheduled installment'
            : 'Custom amount'}
        </Typography>

        {previewPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} sx={{ color: ELF_TEAL }} />
            <Typography variant="body2">Loading allocation preview…</Typography>
          </Box>
        )}

        {preview && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              How this payment will apply
            </Typography>
            <Typography variant="body2">
              Charges {fmtMoney(preview.total_applied_to_charges)} · Interest{' '}
              {fmtMoney(preview.total_applied_to_interest)} · Principal{' '}
              {fmtMoney(preview.total_applied_to_principal)}
            </Typography>
            {preview.unapplied_amount > 0.004 && (
              <Typography variant="body2" color="text.secondary">
                Unapplied {fmtMoney(preview.unapplied_amount)}
              </Typography>
            )}
            {preview.warnings?.map((w) => (
              <Alert key={w} severity="warning" sx={{ mt: 1 }}>
                {w}
              </Alert>
            ))}
          </Box>
        )}

        {!session && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => void handleStartCard()}
              disabled={
                previewPending || !preview || sessionPending || completing
              }
              sx={elfPrimaryButtonSx}
            >
              {sessionPending ? 'Starting checkout…' : 'Enter card details'}
            </Button>
            <Button
              onClick={() => navigate('/portal/loans/pay')}
              disabled={sessionPending || completing}
            >
              Back
            </Button>
          </Stack>
        )}

        {session && (
          <Box sx={{ mt: 2 }}>
            {completing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Finalizing your payment…
              </Alert>
            )}
            <PayflowHostedCardFrame
              iframeUrl={session.iframe_url}
              sessionId={session.session_id}
              onMessage={onPayflowMessage}
            />
            <Button
              sx={{ mt: 2 }}
              onClick={() => {
                setSession(null);
                setError(null);
              }}
              disabled={completing}
            >
              Cancel card entry
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoanPayConfirm;
